import { useClipboard } from '@mantine/hooks';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldValues, useWatch } from 'react-hook-form';

import { BaseInputProps } from './GetFormInput';

// Hex/Decimal dual input that ALWAYS stores decimal (base10) value into the form field.
// Default editing mode is hexadecimal. When hex is edited, decimal reflects conversion live.
// Paste of MAC-like address (e.g. 04:ED:09:32:62:69:84) is sanitized (colons, dashes, spaces removed).
// Provides copy buttons for both representations.
export function FormInputHex<T extends FieldValues>({
    control,
    disabled,
    helperText,
    label,
    required,
    small,
    validation,
    value,
}: Readonly<BaseInputProps<T>>) {
    const externallyDisabled = false;
    const clipboard = useClipboard({ timeout: 1200 });

    const [mode, setMode] = useState<'hex' | 'decimal'>('hex');
    const [decimalValue, setDecimalValue] = useState<number | null>(null);
    const [hexDisplay, setHexDisplay] = useState<string>('');
    const lastDecRef = useRef<number | null>(null);

    const hexInputRef = useRef<HTMLInputElement | null>(null);
    const decimalInputRef = useRef<HTMLInputElement | null>(null);

    const sanitizeHex = (raw: string): string => {
        if (!raw) {
            return '';
        }
        let cleaned = raw.trim().toUpperCase();
        cleaned = cleaned.replace(/^0X/, '');
        cleaned = cleaned.replaceAll(/[:\-\s]/g, '');
        cleaned = cleaned.replaceAll(/[^0-9A-F]/g, '');
        return cleaned;
    };

    const toHex = (dec: number | null): string => {
        if (dec === null || isNaN(dec)) {
            return '';
        }
        if (dec < 0) {
            return '';
        }
        return dec.toString(16).toUpperCase();
    };

    const toDecimal = (hex: string): number | null => {
        if (!hex) {
            return null;
        }
        try {
            const big = BigInt('0x' + hex);
            const num = Number(big);
            if (big > BigInt(Number.MAX_SAFE_INTEGER)) {
                return num;
            }
            return num;
        } catch {
            return null;
        }
    };

    const formFieldValue = useWatch({ control, name: value });

    const hexValue = useMemo(() => toHex(decimalValue), [decimalValue]);

    useEffect(() => {
        let numeric: number | null = null;
        if (formFieldValue === undefined || formFieldValue === null) {
            numeric = null;
        } else if (typeof formFieldValue === 'number') {
            numeric = formFieldValue;
        } else if (typeof formFieldValue === 'string') {
            // Try to parse as hex
            const cleaned = sanitizeHex(formFieldValue);
            numeric = toDecimal(cleaned);
            if (numeric === null) {
                // Try as decimal string
                const dec = Number.parseFloat(formFieldValue);
                if (!isNaN(dec) && dec >= 0) {
                    numeric = dec;
                }
            }
        }
        if (numeric !== lastDecRef.current) {
            lastDecRef.current = numeric;
            setDecimalValue(numeric);
            setHexDisplay(toHex(numeric) || '');
        }
    }, [formFieldValue]);

    useEffect(() => {
        if (mode === 'hex' && hexInputRef.current) {
            hexInputRef.current.focus();
        } else if (mode === 'decimal' && decimalInputRef.current) {
            decimalInputRef.current.focus();
        }
    }, [mode]);

    const debounceMs = 500;
    const hexTimerRef = useRef<NodeJS.Timeout | null>(null);
    const decTimerRef = useRef<NodeJS.Timeout | null>(null);

    const scheduleHexApply = (sanitized: string, onChange: (value: unknown) => void) => {
        if (hexTimerRef.current) {
            clearTimeout(hexTimerRef.current);
        }
        hexTimerRef.current = setTimeout(() => {
            const dec = toDecimal(sanitized);
            if (dec !== lastDecRef.current) {
                lastDecRef.current = dec;
                setDecimalValue(dec);
                setHexDisplay(toHex(dec) || '');
            } else {
                setDecimalValue(dec); // ensure null updates propagate
                setHexDisplay(toHex(dec) || '');
            }
            onChange(dec !== null ? dec : null);
        }, debounceMs);
    };

    const handleHexChange = (raw: string, onChange: (value: unknown) => void) => {
        const sanitized = sanitizeHex(raw);
        setHexDisplay(sanitized);
        scheduleHexApply(sanitized, onChange);
    };

    const scheduleDecApply = (dec: number | null, onChange: (value: unknown) => void) => {
        if (decTimerRef.current) {
            clearTimeout(decTimerRef.current);
        }
        decTimerRef.current = setTimeout(() => {
            if (dec !== lastDecRef.current) {
                lastDecRef.current = dec;
                setDecimalValue(dec);
                setHexDisplay(toHex(dec) || '');
            } else {
                setDecimalValue(dec);
                setHexDisplay(toHex(dec) || '');
            }
            if (dec === null) {
                onChange(null);
                return;
            }
            onChange(dec);
        }, debounceMs);
    };

    const handleDecimalChange = (raw: string, onChange: (value: unknown) => void) => {
        if (raw === '') {
            setHexDisplay('');
            scheduleDecApply(null, onChange);
            return;
        }
        const dec = Number.parseFloat(raw);
        if (isNaN(dec) || dec < 0) {
            setHexDisplay('');
            scheduleDecApply(null, onChange);
            return;
        }
        // Immediate reflect typed number locally (without conversion) for responsiveness
        setDecimalValue(dec);
        setHexDisplay(toHex(dec) || '');
        scheduleDecApply(dec, onChange);
    };

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (hexTimerRef.current) {
                clearTimeout(hexTimerRef.current);
            }
            if (decTimerRef.current) {
                clearTimeout(decTimerRef.current);
            }
        };
    }, []);

    const copyHex = () => {
        clipboard.copy(hexValue);
    };
    const copyDecimal = () => {
        clipboard.copy(decimalValue === null ? '' : String(decimalValue));
    };

    const hexDisplayValue = hexDisplay;
    const decimalDisplayValue = decimalValue === null ? '' : String(decimalValue);

    const isComponentDisabled = disabled || externallyDisabled;

    const validationRules = useMemo(() => {
        return {
            required: required ? `${label ?? value} cannot be empty` : false,
            validate: (v: unknown) => {
                if (v === null || v === undefined || v === '') {
                    return required ? `${label ?? value} cannot be empty` : true;
                }
                if (typeof v !== 'number' || isNaN(v)) {
                    return 'Value must be a valid number';
                }
                const parsed = validation?.safeParse(v);
                return parsed?.success ? true : parsed?.error.errors[0]?.message;
            },
        };
    }, [required, label, value, validation]);

    return (
        <Controller
            control={control}
            name={value}
            render={(controllerRenderProps) => {
                const {
                    field: { onBlur, onChange },
                    fieldState: { error },
                } = controllerRenderProps;
                const hexTooltipTitle = clipboard.copied && hexValue ? 'Copied' : 'Copy hex';
                const decimalTooltipTitle = clipboard.copied && decimalValue !== null ? 'Copied' : 'Copy decimal';
                return (
                    <Stack spacing={1}>
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <ToggleButtonGroup
                                exclusive
                                onChange={(_, val: 'hex' | 'decimal' | null) => {
                                    if (val === 'hex' || val === 'decimal') {
                                        setMode(val);
                                    }
                                }}
                                size={small ? 'small' : 'medium'}
                                value={mode}
                            >
                                <ToggleButton disabled={isComponentDisabled} sx={{ px: 1.5 }} value="hex">
                                    Hex
                                </ToggleButton>
                                <ToggleButton disabled={isComponentDisabled} sx={{ px: 1.5 }} value="decimal">
                                    Dec
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: mode === 'hex' ? 'text.primary' : 'text.secondary',
                                            fontWeight: 600,
                                        }}
                                        variant="caption"
                                    >
                                        Hexadecimal
                                    </Typography>
                                    <Tooltip placement="top" title={hexTooltipTitle}>
                                        <span>
                                            <IconButton disabled={!hexValue} onClick={copyHex} size="small">
                                                <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <TextField
                                    disabled={isComponentDisabled || mode !== 'hex'}
                                    error={Boolean(error?.message)}
                                    fullWidth
                                    helperText={error?.message ?? helperText}
                                    inputRef={hexInputRef}
                                    onBlur={onBlur}
                                    onChange={(e) => handleHexChange(e.target.value, onChange)}
                                    onPaste={(e) => {
                                        const data = e.clipboardData.getData('text');
                                        e.preventDefault();
                                        handleHexChange(data, onChange);
                                    }}
                                    placeholder="e.g. AA:BB:CC:DD:EE:FF"
                                    size={small ? 'small' : 'medium'}
                                    value={hexDisplayValue}
                                />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: mode === 'decimal' ? 'text.primary' : 'text.secondary',
                                            fontWeight: 600,
                                        }}
                                        variant="caption"
                                    >
                                        Decimal (Stored)
                                    </Typography>
                                    <Tooltip placement="top" title={decimalTooltipTitle}>
                                        <span>
                                            <IconButton
                                                disabled={decimalValue === null}
                                                onClick={copyDecimal}
                                                size="small"
                                            >
                                                <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <TextField
                                    disabled={isComponentDisabled || mode !== 'decimal'}
                                    error={Boolean(error?.message)}
                                    fullWidth
                                    helperText={error?.message ?? helperText}
                                    inputRef={decimalInputRef}
                                    onBlur={onBlur}
                                    onChange={(e) => handleDecimalChange(e.target.value, onChange)}
                                    placeholder="Decimal value"
                                    size={small ? 'small' : 'medium'}
                                    type="number"
                                    value={decimalDisplayValue}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                            <Typography sx={{ color: 'text.secondary' }} variant="caption">
                                Stored as base10. Mode switching does not alter stored value.
                            </Typography>
                        </Box>
                    </Stack>
                );
            }}
            rules={validationRules}
        />
    );
}
